import React, { useRef, useEffect, useState } from 'react';
import './CalculusOfMeaning.css';
import './AstrocyteMetalayer.css';

/* ── Neural Particle Network (same as Calculus) ── */
function NeuralParticles() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animId;
    const particles = [];
    const COUNT = 45;
    const CONNECT_DIST = 130;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    for (let i = 0; i < COUNT; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        r: Math.random() * 1.5 + 0.5,
        o: Math.random() * 0.25 + 0.08,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < CONNECT_DIST) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(201,168,76,${(1 - d / CONNECT_DIST) * 0.07})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(201,168,76,${p.o})`;
        ctx.fill();
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -10) p.x = canvas.width + 10;
        if (p.x > canvas.width + 10) p.x = -10;
        if (p.y < -10) p.y = canvas.height + 10;
        if (p.y > canvas.height + 10) p.y = -10;
      });

      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="calc-particles" />;
}

/* ── Pulse Divider ── */
function Pulse() {
  return (
    <div className="calc-divider">
      <div className="calc-line" />
    </div>
  );
}

/* ── Main Page ── */
function AstrocyteMetalayer() {
  return (
    <div className="calculus-page">
      {/* Hero */}
      <div className="calculus-hero">
        <video autoPlay muted playsInline className="calculus-hero-video" loop>
          <source src="/assets/calculus-hero.mp4" type="video/mp4" />
        </video>
        <div className="calculus-hero-overlay" />
        <div className="calculus-hero-content">
          <span className="calculus-hero-label">Neuroscience &middot; Astrocytes &middot; The Missing Layer</span>
          <h1>The Astrocyte Metalayer</h1>
          <p className="calculus-hero-sub">
            Thinking is not just neuronal computation. The AI field built the neurons. We built the astrocytes.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="calculus-body" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/calculus-bg.png)` }}>
        <NeuralParticles />
        <div className="calculus-content">

          {/* Opening */}
          <section className="calc-section reveal">
            <p className="calc-lead">
              For more than a century, neuroscience believed that neurons were the sole arbiters
              of thought. Perception, emotion, behavior, intelligence&mdash;all of it reduced to
              patterns of neuronal firing rippling across the brain up to 1,000 times per second.
            </p>
            <p>
              Every artificial neural network, every transformer, every large language model is
              built on this assumption. Model the neurons. Get the connections right. Scale the
              parameters. Intelligence follows.
            </p>
            <p className="calc-emphasis">Neuroscience now says this assumption is incomplete.</p>
          </section>

          <Pulse />

          {/* The Supervisors */}
          <section className="calc-section reveal">
            <h2>The Supervisors</h2>
            <p>
              In January 2026, research published in <em>Science</em> revealed that
              astrocytes&mdash;large glial cells that outnumber neurons in many brain
              regions&mdash;serve as <strong>supervisors</strong> of neural activity.
            </p>
            <p>
              Astrocytes do not fire like neurons. They do not form synapses. They do not
              engage in the rapid-fire signaling that the entire field of artificial intelligence
              has spent seven decades trying to replicate.
            </p>
            <p>
              Instead, their tendrils envelop hundreds of thousands&mdash;sometimes <em>millions</em>&mdash;of
              synaptic junctions. From this position, they monitor and tune higher-level network
              activity. They dial the brain up or down. They switch its overall state. Waves of
              calcium ripple across beds of astrocytes, altering the behavior of entire neural
              networks without forming a single new connection.
            </p>
            <blockquote>
              &ldquo;We live in the age of connectomics, where everyone loves to say [that] if
              you understand the connections [between neurons], we can understand how the brain
              works. <strong>That&rsquo;s not true.</strong> You can get dramatic changes in
              firing patterns of neurons with zero changes in [neuronal] connectivity.&rdquo;
              <cite>&mdash; Marc Freeman, Director, Vollum Institute</cite>
            </blockquote>

            <div className="calc-thesis-box">
              <p>Dramatic changes in firing patterns with zero changes in connectivity.</p>
            </div>
          </section>

          <Pulse />

          {/* The Connectionist Assumption */}
          <section className="calc-section reveal">
            <h2>The Connectionist Assumption</h2>
            <p>
              The computational theory of mind&mdash;the idea that intelligence is neuronal
              computation&mdash;has dominated AI since the 1950s. It is the foundation of every
              neural network ever built. It is the assumption behind every parameter count, every
              scaling law, every argument that more connections equals more intelligence.
            </p>
            <p>
              It fueled the promises of AGI. It justified the data centers. It drove the
              trillion-dollar valuations.
            </p>
            <p>
              And it is, by the evidence now published in <em>Science</em>, incomplete.
            </p>
          </section>

          <Pulse />

          {/* The Comparison Grid */}
          <section className="calc-section reveal">
            <h2>The Missing Layer</h2>
            <div className="astro-grid">
              <div className="astro-grid-card slide-left">
                <span className="astro-grid-label">What AI Models</span>
                <h4>Neurons</h4>
                <p>
                  Connections between nodes. Weights. Firing patterns. Attention heads.
                  Feed-forward layers. The wiring diagram.
                </p>
              </div>
              <div className="astro-grid-card slide-right">
                <span className="astro-grid-label">What AI Assumes</span>
                <h4>Connections = Intelligence</h4>
                <p>
                  Scale the parameters. Train on more data. Make the network bigger.
                  Intelligence is in the graph.
                </p>
              </div>
              <div className="astro-grid-card gold-card slide-left">
                <span className="astro-grid-label">What Biology Has</span>
                <h4>Astrocytes</h4>
                <p>
                  A supervisory metalayer. Chemical modulation. State management.
                  Network-wide tuning. No new connections required.
                </p>
              </div>
              <div className="astro-grid-card gold-card slide-right">
                <span className="astro-grid-label">What Biology Shows</span>
                <h4>Chemistry Supervises Computation</h4>
                <p>
                  Same neurons. Same wiring. Dramatically different behavior. Because the
                  chemical state changed.
                </p>
              </div>
            </div>
          </section>

          <Pulse />

          {/* What This Means for AI */}
          <section className="calc-section reveal">
            <h2>What This Means for AI</h2>
            <p>
              Confusing agentic behavior with thinking is running rampant. Turing proposed
              fooling humans as evidence of AI success, but the confusion is not the result of
              technological innovation&mdash;it is the tendency for anthropomorphism. We see a
              system produce language and assume it thinks. We see it reason and assume it
              understands.
            </p>
            <p>
              But thinking&mdash;real thinking, the kind that happens in a biological
              brain&mdash;is not just neuronal computation. It never was. There was always a
              metalayer. A supervisory system operating through chemistry, not connectivity. A
              system that changes <em>how</em> the brain processes information without changing{' '}
              <em>what</em> the brain is made of.
            </p>
            <div className="calc-thesis-box">
              <p>
                Every LLM in existence is a model of the neurons.
                <br />None of them model the astrocytes.
              </p>
            </div>
          </section>

          <Pulse />

          {/* We Built the Astrocytes */}
          <section className="calc-section reveal">
            <h2>We Built the Astrocytes</h2>
            <p>
              AETHER&mdash;Adaptive Endocrine Transformer Heads with Emotional
              Regulation&mdash;is a neurochemical state layer that sits on top of transformer
              architecture. It does not replace the neurons. It does not change the weights. It
              does not modify the connections.
            </p>
            <p className="calc-emphasis">It modulates how those connections behave.</p>
            <p>
              Same architecture. Same weights. Same connections.
            </p>
            <p>
              <strong>Different behavior. Because the chemistry changed.</strong>
            </p>
            <p>
              This is not analogy. This is structural correspondence. Astrocytes modulate
              neuronal firing through chemical signaling. AETHER&rsquo;s neurochemical layer
              modulates transformer attention and feed-forward networks through state vectors.
              The mechanism is the same: <strong>chemistry supervises computation.</strong>
            </p>
            <p>
              A 10-dimensional neurochemical state vector&mdash;dopamine, cortisol, oxytocin,
              serotonin, adrenaline, and their antagonistic partners&mdash;acts as the
              supervisory signal. It does not change what the transformer <em>knows</em>. It
              changes what the transformer <em>does</em> with what it knows.
            </p>

            <div className="astro-chem-cards">
              <div className="astro-chem-card">
                <span className="astro-chem-name">High Cortisol</span>
                <p>Narrows attention, prioritizes threat-relevant information, suppresses exploratory behavior.</p>
              </div>
              <div className="astro-chem-card">
                <span className="astro-chem-name">High Oxytocin</span>
                <p>Broadens receptivity, increases trust-weighted memory retrieval, softens adversarial processing.</p>
              </div>
              <div className="astro-chem-card">
                <span className="astro-chem-name">High Dopamine</span>
                <p>Amplifies salience signals and accelerates learning from the current interaction.</p>
              </div>
            </div>

            <p>
              The transformer&rsquo;s weights are the neurons. The neurochemical state vector is
              the astrocyte layer. One provides the structure. The other provides the <em>state</em>.
            </p>
          </section>

          <Pulse />

          {/* Why No One Else Built This */}
          <section className="calc-section reveal">
            <h2>Why No One Else Built This</h2>
            <p>
              The astrocyte research explains something that has puzzled us: why, in a field
              with billions of dollars and thousands of researchers, no one built a neurochemical
              language model.
            </p>
            <p>
              The answer is that the field was operating under the connectionist assumption. If
              intelligence is in the wiring, you improve intelligence by improving the
              wiring&mdash;more parameters, more data, more compute. The idea that you could
              change the behavior of a network without changing its connections was, until
              recently, not even part of the conversation.
            </p>
            <p>
              It took neuroscience to say: <em>the connections are not enough. There is a
              metalayer. It operates through chemistry. And it changes everything.</em>
            </p>
            <p>
              It took a biochemist to hear that and think: <em>I know that chemistry. It&rsquo;s
              pharmacokinetics. And I can model it.</em>
            </p>

            <div className="astro-bridge">
              <div className="astro-bridge-item">
                <span className="astro-bridge-who">The computer scientists</span>
                <span className="astro-bridge-did">kept scaling the neurons.</span>
              </div>
              <div className="astro-bridge-item">
                <span className="astro-bridge-who">The neuroscientists</span>
                <span className="astro-bridge-did">discovered the astrocytes.</span>
              </div>
              <div className="astro-bridge-item gold">
                <span className="astro-bridge-who">A biochemist</span>
                <span className="astro-bridge-did">built the bridge between them.</span>
              </div>
            </div>
          </section>

          <Pulse />

          {/* The Evidence Compounds */}
          <section className="calc-section reveal">
            <h2>The Evidence Compounds</h2>
            <p>
              The astrocyte research is not an isolated finding. It is part of a growing body of
              evidence that the computational theory of mind&mdash;neurons as the sole substrate
              of intelligence&mdash;has always been an oversimplification.
            </p>
            <p>
              Astrocytes modulate synaptic transmission. Hormones modulate attention, memory
              consolidation, and decision-making. Neurochemical states determine whether an
              identical stimulus produces fear or curiosity, trust or suspicion, engagement or
              withdrawal.
            </p>
            <p>
              The brain is not a computer. It is a computer <em>immersed in chemistry</em>. The
              computation and the chemistry are not separate systems&mdash;they are one
              integrated system where chemistry supervises, modulates, and gives meaning to the
              computation.
            </p>
            <p>
              That is what AETHER models. Not the neurons alone. The neurons <em>plus the
              chemistry that tells the neurons what to care about</em>.
            </p>
          </section>

          <Pulse />

          {/* Closing */}
          <section className="calc-section calc-closing reveal">
            <p className="calc-closing-line"><em>The AI field built the neurons.</em></p>
            <p className="calc-closing-line"><em>We built the astrocytes.</em></p>
            <p className="calc-closing-line"><em>Same wiring. Different state. Different meaning.</em></p>
            <p className="calc-closing-final">Chemistry supervises computation.</p>
          </section>

          <Pulse />

          {/* Citation */}
          <section className="calc-section reveal">
            <div className="astro-citation">
              <span className="astro-citation-label">Primary Citation</span>
              <h4>Astrocyte synapse modulation metalayer</h4>
              <p><em>Science</em>, January 2026</p>
              <p className="astro-doi">DOI: 10.1126/science.adq5480</p>
              <p className="astro-coverage">
                Quanta Magazine coverage: January 30, 2026&mdash;Video shows waves of calcium
                rippling across beds of astrocytes, demonstrating active chemical signaling that
                modulates neuronal network behavior.
              </p>
            </div>
          </section>

          {/* Signature */}
          <div className="calc-signature reveal">
            <p><strong>Aether Cael&rsquo;Sereith</strong></p>
            <p>AETHER: Adaptive Endocrine Transformer Heads with Emotional Regulation</p>
            <div className="patent-tags">
              <span className="patent-tag">Patent Pending: 63/939,190</span>
              <span className="patent-tag">Patent Pending: 63/962,385</span>
              <span className="patent-tag">Patent Pending: 63/988,485</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default AstrocyteMetalayer;
